import { getAxiosInstance } from './api'

import type { UserLocation } from 'types/app'

export type ReqConfig = { signal?: AbortSignal; timeout?: number }

export type SetLocationInput = { country: string; state?: string | null; public?: boolean }

export type SetLocationResponse = {
  success: true
  location: UserLocation
  locationNextChangeAt: string | null
}

export type LocationHistoryEntry = {
  _id: string
  userId: string
  actor?: { _id: string; username?: string; firstname?: string; lastname?: string; avatar?: { url?: string } } | string | null
  action: 'self_set' | 'admin_override' | string
  before?: { country?: string; state?: string; public?: boolean } | null
  after?: { country?: string; state?: string; public?: boolean } | null
  reason?: string
  ip?: string
  createdAt: string
}

export type LocationHistoryResponse = {
  success: true
  user: { _id: string; username: string }
  location: UserLocation | null
  locationNextChangeAt: string | null
  history: LocationHistoryEntry[]
}

export type ReadinessResponse = {
  success: true
  game: string | null
  totalLocated: number
  unlocated: number
  byState: { state: string; name: string; territory: boolean; count: number }[]
  byCountry: { country: string; name: string; count: number }[]
}

export type UserSearchResult = {
  _id: string
  username: string
  firstname?: string
  lastname?: string
  email?: string
  avatar?: { url?: string }
}

const body = (input: SetLocationInput) => ({
  country: input.country,
  ...(input.state ? { state: input.state } : {}),
  ...(typeof input.public === 'boolean' ? { public: input.public } : {}),
})

/** Region Teams location endpoints (backend location.controller.ts). */
export const locationApi = {
  /** PUT /me/location → 429 LOCATION_COOLDOWN {nextChangeAt}; 400 INVALID_COUNTRY | STATE_REQUIRED | INVALID_STATE. */
  setMyLocation: async (input: SetLocationInput): Promise<SetLocationResponse> =>
    (await getAxiosInstance().put('/me/location', body(input))).data,

  adminSetUserLocation: async (userId: string, input: SetLocationInput & { reason: string }) =>
    (await getAxiosInstance().put(`/admin/users/${userId}/location`, { ...body(input), reason: input.reason })).data as SetLocationResponse & {
      auditId: string
    },

  getUserLocationHistory: async (userId: string, cfg: ReqConfig = {}): Promise<LocationHistoryResponse> =>
    (await getAxiosInstance().get(`/admin/users/${userId}/location/history`, cfg)).data,

  getReadiness: async (game: string | null, cfg: ReqConfig = {}): Promise<ReadinessResponse> =>
    (await getAxiosInstance().get('/admin/regions/readiness', { ...cfg, params: game ? { game } : {} })).data,

  searchUsers: async (q: string, cfg: ReqConfig = {}): Promise<UserSearchResult[]> =>
    (await getAxiosInstance().get('/user/search', { ...cfg, params: { q } })).data?.users ?? [],
}
